/**
 * The line pictograms the chrome needs, inlined as SVG.
 *
 * Inline rather than a sprite or an icon font because they inherit `currentColor`
 * — the tab bar's active/idle states are a colour change on the label and its
 * icon together, and that only works if the stroke is text colour. The SVG frame
 * itself lives in `icons/Icon`, shared with `plants/PlantIcons`.
 *
 * These are placeholders in the mockups' visual language (1.5px strokes, round
 * caps, botanical geometry) and will be replaced wholesale when the brand icon
 * set arrives. Shape names, props and sizing are the contract; the paths are not.
 */

import Icon from '../icons/Icon';

export function HomeIcon(props) {
    return (
        <Icon {...props}>
            <path d="M4 10.5 12 4l8 6.5" />
            <path d="M6 9.5V20h12V9.5" />
        </Icon>
    );
}

export function PlantsIcon(props) {
    return (
        <Icon {...props}>
            <path d="M12 21v-8" />
            <path d="M12 13c0-3 2-5 5-5.5C17 11 15 13 12 13Z" />
            <path d="M12 15c0-2.5-1.7-4.3-4.5-4.8C7.5 13.5 9.4 15 12 15Z" />
        </Icon>
    );
}

export function LearnIcon(props) {
    return (
        <Icon {...props}>
            <path d="M4 5.5h6a2 2 0 0 1 2 2V20a2 2 0 0 0-2-2H4Z" />
            <path d="M20 5.5h-6a2 2 0 0 0-2 2V20a2 2 0 0 1 2-2h6Z" />
        </Icon>
    );
}

export function CommunityIcon(props) {
    return (
        <Icon {...props}>
            <circle cx="9" cy="9" r="2.6" />
            <path d="M4 19c0-2.6 2.2-4.4 5-4.4s5 1.8 5 4.4" />
            <path d="M15.5 7.2a2.6 2.6 0 0 1 0 5" />
            <path d="M17 14.9c1.9.5 3 1.9 3 4.1" />
        </Icon>
    );
}

export function GardenIcon(props) {
    return (
        <Icon {...props}>
            <path d="M6 11h12l-1.2 8.2a1.5 1.5 0 0 1-1.5 1.3H8.7a1.5 1.5 0 0 1-1.5-1.3Z" />
            <path d="M12 11V7" />
            <path d="M12 7c0-1.8 1.2-3 3-3.2C15 5.6 13.8 7 12 7Z" />
        </Icon>
    );
}

/**
 * The little two-leaf sprig: the landing's divider ornament and the mark on an
 * empty state.
 *
 * This was called `BrandMark` while the brand had no mark. It is not one — the
 * brand mark is the lavender illustration (`LavenderMark` below, design system
 * §6.3). This is a line ornament that takes `currentColor`, which is exactly
 * what the two places it is used need and what a detailed botanical raster
 * could not give them.
 */
export function LeafGlyph({ size = 36, ...rest }) {
    return (
        <Icon size={size} {...rest}>
            <path d="M12 21V7" />
            <path d="M12 11c0-2.6 1.8-4.4 4.6-4.9C16.6 8.7 14.8 11 12 11Z" />
            <path d="M12 14.5c0-2.6-1.8-4.4-4.6-4.9C7.4 12.2 9.2 14.5 12 14.5Z" />
            <path d="M12 7c0-2 1.3-3.4 3.4-3.8C15.4 5 14.1 7 12 7Z" />
        </Icon>
    );
}

const MARK_SOURCE = '/assets/brand/lavender-mark';

/**
 * The brand mark: the lavender illustration itself (§6.3).
 *
 * A raster and not an SVG on purpose — §7.2 asks for transparent PNG/WebP for
 * the botanical artwork and reserves SVG for art that is genuinely vector-safe.
 * This is a painted illustration with soft edges and shading; traced to vector it
 * would be both larger and worse.
 *
 * `alt=""`: everywhere this appears, the wordmark is beside it saying the name.
 * The intrinsic size is the artwork's own, so the header does not reflow when it
 * loads; CSS sets the drawn height.
 */
export function LavenderMark({ className, sizes = '30px' }) {
    return (
        <img
            className={className}
            src={`${MARK_SOURCE}-60.webp`}
            srcSet={`${MARK_SOURCE}-60.webp 60w, ${MARK_SOURCE}-120.webp 120w, ${MARK_SOURCE}-180.webp 180w`}
            sizes={sizes}
            width="250"
            height="417"
            alt=""
        />
    );
}

/**
 * The landing's scroll affordance, and the language menu's caret — the two places
 * something says "there is more this way".
 */
export function ChevronDownIcon(props) {
    return (
        <Icon size={22} {...props}>
            <path d="m6 10 6 6 6-6" />
        </Icon>
    );
}

export function ChevronRightIcon(props) {
    return (
        <Icon size={22} {...props}>
            <path d="m9.5 5 7 7-7 7" />
        </Icon>
    );
}

/**
 * Saved-to-garden, in the hero. `filled` is the saved state: the outline alone
 * would leave "saved" and "not saved" a stroke-weight apart, which is not a
 * difference at 22px.
 */
export function HeartIcon({ filled = false, ...rest }) {
    return (
        <Icon fill={filled ? 'currentColor' : 'none'} {...rest}>
            <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 1 0-7.8 7.8l1 1.1 7.8 7.7 7.8-7.7 1-1.1a5.5 5.5 0 0 0 0-7.8Z" />
        </Icon>
    );
}

export function ShareIcon(props) {
    return (
        <Icon {...props}>
            <path d="M12 15.5V3.6" />
            <path d="m8.4 7.2 3.6-3.6 3.6 3.6" />
            <path d="M7.5 10.5H5.6A1.6 1.6 0 0 0 4 12.1v7.3a1.6 1.6 0 0 0 1.6 1.6h12.8a1.6 1.6 0 0 0 1.6-1.6v-7.3a1.6 1.6 0 0 0-1.6-1.6h-1.9" />
        </Icon>
    );
}

export function PlusCircleIcon(props) {
    return (
        <Icon {...props}>
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v8M8 12h8" />
        </Icon>
    );
}

export function MinusCircleIcon(props) {
    return (
        <Icon {...props}>
            <circle cx="12" cy="12" r="9" />
            <path d="M8 12h8" />
        </Icon>
    );
}

export function CheckCircleIcon(props) {
    return (
        <Icon {...props}>
            <circle cx="12" cy="12" r="9" />
            <path d="m8.2 12.3 2.5 2.5 5.1-5.6" />
        </Icon>
    );
}
